import React, { useState, useCallback, useEffect } from "react";

import { Helmet } from 'react-helmet-async';

import {
    Box,
    Paper,
    Divider,
    TextField,
    Grid,
    Typography,
    Switch,
    FormControlLabel,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    ListSubheader,
    List,
    ListItem,
    IconButton,
    ListItemButton,
    styled,
    Card,
    Button,
    Tooltip,
    Checkbox,
    Chip,
    ListItemText,
    OutlinedInput
} from '@mui/material';

import {
    LoadingButton
} from '@mui/lab';

import { grey, red } from "@mui/material/colors";

import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from 'formik';
import CommonBreadcumbs from '../../common/CommonBreadcumbs';
import CommonSectionHeader from "../../common/CommonSectionHeader";
import CommonRestricted from '../../common/CommonRestricted';
import { CommonIcons, icons } from '../../common/CommonIcons';

import { mergeSchema } from "../../../utils/dataSchema"
import { spliceString } from "../../../utils/formatString";

import { useUsers, userTypes, targetTypes } from "../../../hooks/api/useUsers";
import { useCustomFields, customFieldModules, customFieldFunctions } from '../../../hooks/api/useCustomFields';
import { useBranches, branchTypes, targetTypes as branchTargetTypes } from "../../../hooks/api/useBranches";
import { useBranchUsers, branchUserTypes, targetTypes as branchUserTargetTypes } from '../../../hooks/api/useBranchUsers';
import { useRates, rateTypes } from "../../../hooks/api/useRates";

import { useSystemPrompt } from "../../../hooks/useSystemPrompt";
import { useAppContextFeatures } from '../../../context/AppContext';
import { initialValues, initialPasswordValues, validationSchema, validationPasswordSchema } from "../../../schema/users/customerSchema";
import { inputTypes, inputTypesDef } from "../../../schema/customFields/customFieldSchema"

import { useSystemRoutes, routeTypes } from '../../../hooks/useSystemRoutes';
import { useSystemHead, headTitles } from '../../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from '../../../hooks/usePermissions';
import { useTranslations } from '../../../hooks/useTranslations';

import NewBranch from "../../@user_branches/customers/NewBranch"
import UpdateBranch from "../../@user_branches/customers/UpdateBranch";
import NewBranchUser from "../../@branch_users/customers/NewBranchUser";
import UpdateBranchUser from "../../@branch_users/customers/UpdateBranchUser";

import { useStateCustomFields } from "../../../hooks/state/useStateCustomFields";
import { useAuthAccess } from "../../../hooks/useAuthAccess";

const StyledCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(2)
}));

const StyledCard2 = styled(Card)(({ theme }) => ({
    padding: theme.spacing(0)
}));

const StyledListHeader = styled(ListSubheader)(({ theme }) => ({
    backgroundColor: grey[100],
    textTransform: 'capitalize',
}));

export default function UpdateCustomer({
    routeType,
    isOwnProfile = false
}) {
    const { t } = useTranslations({ ns: ["common", "@user", "prompt"] })

    const { authData } = useAuthAccess()

    let { id } = useParams();

    // if the mode is only update own profile
    if (isOwnProfile) {
        id = authData?.profile?.user?.id
    }

    const { promptMessage } = useSystemPrompt();
    const { isLoading, get, update, filterOperatorTypes: filterCustomer } = useUsers({ userType: userTypes.customer });
    const { list: listRates } = useRates({ rateType: rateTypes.taxRate });

    const {
        list: listBranches,
        remove: removeBranch,
        filterOperatorTypes
    } = useBranches({ branchType: branchTypes.customer, targetType: branchTargetTypes.customer });
    const {
        list: listBranchUsers,
        remove: removeBranchUser,
        filterOperatorTypes: filterBranchUser
    } = useBranchUsers({ branchUserType: branchUserTypes.customer, targetType: branchUserTargetTypes.customer });
    const { list: listCustomFields } = useCustomFields({ customFieldModule: customFieldModules.user, customFieldFunction: customFieldFunctions.user.customer })

    const { isPermissionAllow } = usePermissions(
        routeType,
        scopeIds.user.id,
        scopeTypes[scopeIds.user.id].customer.id
    )

    const {
        dataCustomFields,

        getInitialFormStateRows,
        getSubmitFormDataFromRows,

        getCustomFieldOptionNameByIds,

        RenderCustomFieldSelectionTextbox,
        RenderCustomFieldSelectionDropdown,
        RenderCustomFieldSelectionMultiDropdown
    } = useStateCustomFields(
        {
            fieldModule: customFieldModules.user,
            moduleFunction: customFieldFunctions.user.customer
        }
    )

    const { setSystemLoading, openCustomDrawer, closeCustomDrawer } = useAppContextFeatures()

    const navigate = useNavigate();
    const [savedValues, setSavedValues] = useState()
    const { getRoute } = useSystemRoutes(routeType);
    const [dataBranches, setDataBranches] = useState([])
    const [dataBranchUsers, setDataBranchUsers] = useState([])
    const { getHtmlHead } = useSystemHead(routeType)
    const [customFields, setCustomFields] = useState([])
    const [dataTaxRate, setDataTaxRate] = useState([])

    const [checked, setChecked] = useState(false);

    // if checkbox is true only merge those validation together
    const getSchema = () => {
        if (checked === true) {
            return mergeSchema(validationSchema, validationPasswordSchema);
        }
        return validationSchema
    }

    // get check box action and save the state for the action clicked
    const handleAllowChangePassword = (event) => {
        setChecked(event.target.checked);
    };

    const loadData = useCallback(async (id) => {

        setSystemLoading(true)

        try {

            const { data } = await get({ id, userType: userTypes.customer, dataTypesArray: ["exclude_tax_rate"] })
            const { customer, custom_fields: savedCustomFields = [] } = data;

            console.log("Data", data)

            const cf = await getInitialFormStateRows({
                savedCustomFields,
            })

            setSavedValues({
                username: customer.username ? customer.username : "",
                firstname: customer.firstname ? customer.firstname : '',
                lastname: customer.lastname ? customer.lastname : '',
                contactno: customer.contact ? customer.contact : '',
                address: customer.address ? customer.address : '',
                email: customer.email ? customer.email : '',
                status: customer?.status === '1',
                companyname: customer?.company_name ? customer.company_name : '',
                regno: customer?.reg_no ? customer.reg_no : '',
                secondRegNo: customer?.secondary_reg_no ? customer.secondary_reg_no : '',
                code: customer.code ? customer.code : '',
                accountcode: customer.account_code ? customer.account_code : '',
                taxregistrationno: customer.tax_registration_no ? customer.tax_registration_no : '',
                taxidentityno: customer.tax_identification_no ? customer.tax_identification_no : '',
                excludeTax: customer.exclude_tax_rates.map(i => i.id),
                remark: customer.remark ? customer.remark : "",
                ...initialPasswordValues,
                rows: cf

            })

        } catch (error) {
            promptMessage(false, `${error?.message}`)
        }

        setSystemLoading(false)

    }, [id])

    const onSubmit = async (currentValues) => {

        try {

            const { rows } = values;

            let cf = getSubmitFormDataFromRows({ rows })

            let data = {
                credential: {
                    firstname: currentValues?.firstname,
                    lastname: currentValues?.lastname,
                    password: currentValues?.password,
                    contact: currentValues?.contactno,
                    email: currentValues?.email,
                    status: currentValues?.status ? 1 : 0,
                },
                customer: {
                    reg_no: currentValues?.regno,
                    secondary_reg_no: currentValues?.secondRegNo,
                    company_name: currentValues?.companyname,
                    code: currentValues?.code,
                    account_code: currentValues?.accountcode,
                    remark: currentValues?.remark,
                    tax_identification_no: currentValues?.taxidentityno,
                    tax_registration_no: currentValues?.taxregistrationno,
                },
                props: {
                    salesperson: "",
                    exclude_tax_rate: currentValues?.excludeTax,
                    marking: []
                },
                custom_fields: cf,

            }
            const response = await update({ id, data, scopeType: "", targetType: targetTypes.customer });

            promptMessage(true, `${response?.message}`)
            // navigate(getRoute("users/customers"))

        } catch (error) {
            promptMessage(false, `Action Failed. (${error?.message})`)
        }
    }

    const fetchDataTaxRate = async () => {

        try {

            const props = {
                limit: -1,
                offset: 0,
                filterValue: [],
                sortInfo: {
                    id: "rate_name",
                    dir: 0,
                }
            }

            const { data } = await listRates(props)

            console.log('Rate', data);

            setDataTaxRate(data?.tax_rates)

        } catch (error) {
            promptMessage(false, `${error?.message}`)
        }

    }

    const fetchDataBranch = async (id) => {

        try {

            const props = {
                limit: -1,
                skip: 0,
                sortInfo: {
                    id: "id",
                    dir: 0,
                },
                filterValue: [
                    {
                        name: "user_id",
                        operator: filterOperatorTypes.eq,
                        value: id
                    }
                ]
            }

            const { data } = await listBranches(props)

            setDataBranches(data?.branches)

        } catch (error) {
            promptMessage(false, `${error?.message}`)
        }

    }

    const fetchDataBranchUser = async (id) => {

        try {

            const props = {
                limit: -1,
                skip: 0,
                sortInfo: {
                    id: "id",
                    dir: 0,
                },
                filterValue: [
                    {
                        name: "parent_user_id",
                        operator: filterBranchUser.eq,
                        value: id
                    }
                ]
            }

            const { data } = await listBranchUsers(props)

            // console.log("data?.branch_users", data?.branch_users)

            setDataBranchUsers(data?.branch_users)

        } catch (error) {
            promptMessage(false, `${error?.message}`)
        }

    }

    useEffect(() => {

        loadData(id);
        fetchDataBranchUser(id)
        fetchDataBranch(id)
        fetchDataBranchUser(id)
        fetchDataTaxRate()

    }, [])

    const formik = useFormik({
        initialValues: savedValues,
        validationSchema: getSchema(),
        enableReinitialize: true,
        onSubmit
    });

    const { values, errors, touched, handleChange, setFieldValue } = formik;

    const getAddNewComponent = (props) => {

        const { branchType, branchUserType } = props

        if (branchType === branchTypes.customer) {
            return (
                <NewBranch
                    routeType={routeType}
                    handleOnSuccess={() => {
                        closeCustomDrawer()
                        fetchDataBranch(id)
                    }}
                />
            )
        }

        if (branchUserType === branchUserTypes.customer) {
            return (
                <NewBranchUser
                    routeType={routeType}
                    handleOnSuccess={() => {
                        closeCustomDrawer()
                        fetchDataBranchUser(id)
                    }}
                />
            )
        }

        return false

    }

    // const getTaxRateOptionNameId = (optionIdArray) => {

    //     if(!optionIdArray) return []

    //     let optionNames = [];

    //     dataTaxRate.forEach((e) => {
    //             if(optionIdArray.includes(e.id)){
    //                 optionNames.push(`${e.rate_name} (${e.tax_percentage}%)`)
    //             }
    //         // })
    //     })

    //     return optionNames
    // }

    const handleItemNew = ({ branchType = '', branchUserType = '' }) => {

        if (!branchType && !branchUserType) return false

        openCustomDrawer({
            show: true,
            anchor: "right",
            title: `${branchType === branchTypes.customer ? t("newBranch") : t("newBranchUser")}`,
            PaperProps: {
                sx: { width: 2 / 3 }
            },
            handleOnClose: () => { },
            ComponentActions:
                <></>,
            ComponentBody: getAddNewComponent({ branchType, branchUserType })

        })

        return true;

    }

    const handleItemEdit = ({ branchType = '', branchUserType = '', itemId }) => {

        if (!branchType && !branchUserType) return false;

        console.log('itemId => ', itemId)

        openCustomDrawer({
            show: true,
            anchor: "right",
            title: `${branchType === branchTypes.customer ? t("updateBranch") : t("updateUser")}`,
            PaperProps: {
                sx: { width: 2 / 3 }
            },
            handleOnClose: () => { },
            ComponentActions:
                <></>,
            ComponentBody:

                (branchType === branchTypes.customer) ?
                    <UpdateBranch
                        routeType={routeType}
                        id={itemId}
                        handleOnSuccess={() => {
                            closeCustomDrawer()
                            fetchDataBranch(id)
                        }}
                    />
                    :
                    <UpdateBranchUser
                        routeType={routeType}
                        id={itemId}
                        handleOnSuccess={() => {
                            closeCustomDrawer()
                            fetchDataBranchUser(id)
                        }}
                    />
        })

        return true;

    }

    const handleItemDelete = async ({ branchType = '', branchUserType = '', itemId }) => {

        setSystemLoading(true)

        try {

            if (branchType === branchTypes.customer) {
                let result = await removeBranch({ ids: [itemId] })
                promptMessage(true, result?.message)
                fetchDataBranch(id)
            }

            if (branchUserType === branchUserTypes.customer) {
                let result = await removeBranchUser({ ids: [itemId] })
                promptMessage(true, result?.message)
                fetchDataBranchUser(id)
            }

        } catch (error) {
            promptMessage(false, `Action Failed (${error?.message})`)
        }

        setSystemLoading(false)

    }

    const RenderEmpty = ({ branchType = '' }) => {

        if (!branchType) return <></>

        return (
            <StyledCard2>
                <Box
                    sx={{
                        width: '100%',
                        position: 'relative',
                        overflow: 'auto',
                        minHeight: 300,
                    }}
                    display={"flex"}
                    flexDirection={"column"}
                    justifyContent={"center"}
                    alignItems={"center"}
                >
                    <Box>
                        {t('youHaveNotCreatedAnyItemYet')}
                    </Box>
                    {
                        isOwnProfile !== true &&
                        <Button
                            variant="outlined"
                            sx={{
                                mt: 1
                            }}
                            onClick={() => handleItemNew({ branchType })}
                        >
                            {t('createNew')}
                        </Button>
                    }
                </Box>
            </StyledCard2>
        )
    }

    const RenderBranchUserEmpty = ({ branchUserType = '' }) => {

        if (!branchUserType) return <></>

        return (
            <StyledCard2>
                <Box
                    sx={{
                        width: '100%',
                        position: 'relative',
                        overflow: 'auto',
                        minHeight: 300,
                    }}
                    display={"flex"}
                    flexDirection={"column"}
                    justifyContent={"center"}
                    alignItems={"center"}
                >
                    <Box>
                        {t('youHaveNotCreatedAnyItemYet')}
                    </Box>
                    {
                        isOwnProfile !== true &&
                        <Button
                            variant="outlined"
                            sx={{
                                mt: 1
                            }}
                            onClick={() => handleItemNew({ branchUserType })}
                        >
                            {t('createNew')}
                        </Button>
                    }
                </Box>
            </StyledCard2>
        )
    }

    const RenderChangePassword = () => {

        if (checked === false) return <></>

        return (
            <>
                <Grid item xs={12} sm={12} md={6} textAlign={"left"}>
                    <TextField
                        margin="dense"
                        size="small"
                        fullWidth
                        name={"password"}
                        label={t("password")}
                        type="password"
                        variant="outlined"
                        value={values.password}
                        onChange={handleChange}
                        error={touched.password && Boolean(errors.password)}
                        helperText={touched.password && errors.password}
                    />
                </Grid>

                <Grid item xs={12} sm={12} md={6} textAlign={"left"}>
                    <TextField
                        margin="dense"
                        size="small"
                        fullWidth
                        name={"passwordconfirm"}
                        label={t("confirmPassword")}
                        type="password"
                        variant="outlined"
                        value={values.passwordconfirm}
                        onChange={handleChange}
                        error={touched.passwordconfirm && Boolean(errors.passwordconfirm)}
                        helperText={touched.passwordconfirm && errors.passwordconfirm}
                    />
                </Grid>
            </>
        )

    }

    const RenderCustomFieldSelection = () => {

        if (!values?.rows) return <></>

        const rows = values.rows

        // if no custom fields just show nothing
        if (values.rows.length <= 0) return <></>

        return (
            <>
                <Grid item xs={12}>
                    <Divider sx={{ mt: 2, mb: 1 }} />
                </Grid>

                <Grid item xs={12} sm={12} md={3} textAlign={"left"}>
                    <CommonSectionHeader
                        title={t('customProperties')}
                        subTitle={t("provideCustomItemInformation")}
                    />
                </Grid>
                <Grid item xs={12} sm={12} md={9}>
                    <List>
                        {
                            values.rows.map((item, index) => {

                                const { rows } = values

                                let itemValues = rows.length ? rows[index] : {}

                                let {
                                    fieldType,
                                    fieldLabel,
                                    fieldKey
                                } = item

                                return (
                                    <ListItem
                                        key={index}
                                        divider
                                        disablePadding
                                        sx={{
                                            py: 1
                                        }}
                                    >
                                        <Grid container spacing={1}>
                                            <Grid item md={3} xs={12} sm={12} display={"flex"} flexDirection={"column"} justifyContent={"center"} alignItems={"center"} >
                                                <Chip
                                                    label={fieldLabel}
                                                    color='primary'
                                                    variant='outlined'
                                                    size='large'
                                                    sx={{
                                                        width: '100%',
                                                        // textAlign: 'left',
                                                        fontSize: 16,
                                                        fontWeight: 800
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item md={9} xs={12} sm={12}>

                                                {
                                                    fieldType === inputTypesDef.textbox &&

                                                    RenderCustomFieldSelectionTextbox({
                                                        index,
                                                        item,
                                                        itemValues,
                                                        inputProps: {
                                                            fullWidth: true,
                                                            name: `rows.${index}.customValue`,
                                                            label: itemValues?.fieldLabel,
                                                            type: "text",
                                                            variant: "outlined",
                                                            value: itemValues?.customValue,
                                                            onChange: (event) => setFieldValue(`rows.${index}.customValue`, event.target.value),
                                                            // error: Boolean(itemErrors?.customValue),
                                                            // helperText: itemErrors?.customValue
                                                        }
                                                    })
                                                }

                                                {
                                                    fieldType === inputTypesDef.dropdown &&

                                                    RenderCustomFieldSelectionDropdown({
                                                        index,
                                                        item,
                                                        itemValues,
                                                        selectProps: {
                                                            labelId: "customValue",
                                                            label: itemValues?.fieldLabel,
                                                            name: `rows.${index}.customArray`,
                                                            onChange: (event) => {
                                                                setFieldValue(`rows.${index}.customArray`, [event.target.value])
                                                            },
                                                            size: 'small',
                                                            margin: 'dense',
                                                            value: itemValues.customArray.length ? itemValues.customArray[0] : ""
                                                        }
                                                    })
                                                }

                                                {
                                                    fieldType === inputTypesDef.multidropdown &&

                                                    RenderCustomFieldSelectionMultiDropdown({
                                                        index,
                                                        item,
                                                        itemValues,
                                                        selectProps: {
                                                            labelId: itemValues?.fieldLabel,
                                                            label: itemValues?.fieldLabel,
                                                            name: `rows.${index}.customArray`,
                                                            onChange: handleChange,
                                                            renderValue: (selected) => {

                                                                return (
                                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                                        {
                                                                            getCustomFieldOptionNameByIds(selected, dataCustomFields, fieldKey).map((i, index) => (
                                                                                <Chip
                                                                                    key={index}
                                                                                    // color={'red'}
                                                                                    label={spliceString({ val: i, maxLength: 20 })}
                                                                                />
                                                                            ))
                                                                        }
                                                                    </Box>
                                                                )

                                                            },
                                                            size: 'small',
                                                            margin: 'dense',
                                                            multiple: true,
                                                            input: <OutlinedInput id="select-multiple-chip" label="Chip" />,
                                                            value: itemValues?.customArray ? itemValues.customArray : []
                                                        }
                                                    })
                                                }

                                            </Grid>
                                        </Grid>
                                    </ListItem>
                                )

                            })
                        }

                    </List>

                </Grid>
            </>
        )
    }

    const RenderFormAuth = () => {

        return (
            <>
                <Grid item xs={12} sm={12} md={3} textAlign={"left"}>
                    <CommonSectionHeader
                        title={t('credential')}
                        subTitle={t('accessAuthentication')}
                    />
                </Grid>

                <Grid item xs={12} sm={12} md={9}>

                    <Grid container spacing={1}>
                        <Grid item xs={12} sm={12} md={6} textAlign={"left"}>
                            <TextField
                                disabled
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"username"}
                                label={t("username")}
                                type="text"
                                variant="outlined"
                                value={values.username}
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid item xs={12} md={6} textAlign="left">
                            <FormControlLabel
                                control={
                                    <Switch
                                        disabled={
                                            isOwnProfile
                                        }
                                        sx={{
                                            ml: 2,
                                            my: 1
                                        }}
                                        name="status"
                                        checked={Boolean(values?.status)}
                                        onChange={(event, checked) => {
                                            setFieldValue("status", checked);
                                        }}
                                    />
                                }
                                label={
                                    Boolean(values.status) ? t("enabled") : t("disabled")
                                }
                            />
                        </Grid>

                        {
                            (isOwnProfile === false) &&
                            <>
                                <Grid item xs={12} sm={12} md={12} textAlign="left">

                                    <Divider>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    onChange={handleAllowChangePassword}
                                                />
                                            }
                                            label={t("updatePassword")}
                                        />
                                    </Divider>

                                </Grid>

                                {
                                    RenderChangePassword()
                                }
                            </>
                        }

                    </Grid>
                </Grid>
            </>
        )
    }

    const RenderFormUser = () => {

        return (
            <>
                <Grid item xs={12} sm={12} md={3} textAlign={"left"}>

                    <CommonSectionHeader
                        title={t('person')}
                        subTitle={t('detailsOfPersonInformation')}
                    />
                </Grid>

                <Grid item xs={12} sm={12} md={9}>
                    <Grid container spacing={1}>

                        <Grid item xs={12} sm={12} md={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"firstname"}
                                label={t("firstName")}
                                type="text"
                                variant="outlined"
                                value={values.firstname}
                                onChange={handleChange}
                                error={touched.firstname && Boolean(errors.firstname)}
                                helperText={touched.firstname && errors.firstname}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"lastname"}
                                label={t("lastName")}
                                type="text"
                                variant="outlined"
                                value={values.lastname}
                                onChange={handleChange}
                                error={touched.lastname && Boolean(errors.lastname)}
                                helperText={touched.lastname && errors.lastname}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"email"}
                                label={t("email")}
                                type="email"
                                variant="outlined"
                                value={values.email}
                                onChange={handleChange}
                                error={touched.email && Boolean(errors.email)}
                                helperText={touched.email && errors.email}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"contactno"}
                                label={t("contactNo")}
                                type="text"
                                variant="outlined"
                                value={values.contactno}
                                onChange={handleChange}
                                error={touched.contactno && Boolean(errors.contactno)}
                                helperText={touched.contactno && errors.contactno}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </>
        )
    }

    const RenderFormCompany = () => {

        return (
            <>
                <Grid item xs={12} sm={12} md={3} textAlign={"left"}>

                    <CommonSectionHeader
                        title={t('company')}
                        subTitle={t('detailsOfCompanyInformation')}
                    />
                </Grid>

                <Grid item xs={12} sm={12} md={9}>
                    <Grid container spacing={1}>
                        <Grid item xs={12} sm={12} md={12}>
                            <TextField
                                disabled={
                                    isOwnProfile === true
                                }
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"companyname"}
                                label={t("companyName")}
                                type="text"
                                variant="outlined"
                                value={values.companyname}
                                onChange={handleChange}
                                error={touched.companyname && Boolean(errors.companyname)}
                                helperText={touched.companyname && errors.companyname}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={6}>
                            <TextField
                                disabled={
                                    isOwnProfile === true
                                }
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"regno"}
                                label={t("primaryRegistrationNo")}
                                type="text"
                                variant="outlined"
                                value={values.regno}
                                onChange={handleChange}
                                error={touched.regno && Boolean(errors.regno)}
                                helperText={touched.regno && errors.regno}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={6}>
                            <TextField
                                disabled={
                                    isOwnProfile === true
                                }
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"secondRegNo"}
                                label={t("secondaryRegistrationNo")}
                                type="text"
                                variant="outlined"
                                value={values.secondRegNo}
                                onChange={handleChange}
                                error={touched.secondRegNo && Boolean(errors.secondRegNo)}
                                helperText={touched.secondRegNo && errors.secondRegNo}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={6}>
                            <TextField
                                disabled={
                                    isOwnProfile === true
                                }
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"code"}
                                label={t("customerCode")}
                                type="text"
                                variant="outlined"
                                value={values.code}
                                onChange={handleChange}
                                error={touched.code && Boolean(errors.code)}
                                helperText={touched.code && errors.code}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={6}>
                            <TextField
                                disabled={
                                    isOwnProfile === true
                                }
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"accountcode"}
                                label={t("accountCode")}
                                type="text"
                                variant="outlined"
                                value={values.accountcode}
                                onChange={handleChange}
                                error={touched.accountcode && Boolean(errors.accountcode)}
                                helperText={touched.accountcode && errors.accountcode}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={6}>
                            <TextField
                                disabled={
                                    isOwnProfile === true
                                }
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"salesperson"}
                                label={t("salesperson")}
                                type="text"
                                variant="outlined"
                                value={values.salesperson}
                                onChange={handleChange}
                                error={touched.salesperson && Boolean(errors.salesperson)}
                                helperText={touched.salesperson && errors.salesperson}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={6}>
                            <TextField
                                disabled={
                                    isOwnProfile === true
                                }
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"remark"}
                                label={t("remark")}
                                type="text"
                                multiline
                                rows={3}
                                variant="outlined"
                                value={values.remark}
                                onChange={handleChange}
                                error={touched.remark && Boolean(errors.remark)}
                                helperText={touched.remark && errors.remark}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </>
        )
    }

    const RenderFormTax = () => {

        if (!values) return <></>

        return (
            <>
                <Grid item xs={12} sm={12} md={3} textAlign={"left"}>

                    <CommonSectionHeader
                        title={t('taxation')}
                        subTitle={t('detailsOfTaxationInformation')}
                    />
                </Grid>

                <Grid item xs={12} sm={12} md={9}>
                    <Grid container spacing={1}>
                        <Grid item xs={12} sm={12} md={6}>
                            <TextField
                                disabled={
                                    isOwnProfile === true
                                }
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"taxidentityno"}
                                label={t("taxIdentificationNo")}
                                type="text"
                                variant="outlined"
                                value={values.taxidentityno}
                                onChange={handleChange}
                                error={touched.taxidentityno && Boolean(errors.taxidentityno)}
                                helperText={touched.taxidentityno && errors.taxidentityno}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={6}>
                            <TextField
                                disabled={
                                    isOwnProfile === true
                                }
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"taxregistrationno"}
                                label={t("taxRegistrationNo")}
                                type="text"
                                variant="outlined"
                                value={values?.taxregistrationno}
                                onChange={handleChange}
                                error={Boolean(errors?.taxregistrationno)}
                                helperText={errors?.taxregistrationno}
                            />
                        </Grid>

                        {
                            (routeType !== routeTypes.customer) &&
                            <Grid item xs={12} sm={12} md={9}>
                                <FormControl
                                    margin="dense"
                                    fullWidth
                                    size="small"
                                    error={touched.excludeTax && Boolean(errors.excludeTax)}
                                >
                                    <InputLabel id="excludeTax">{t('excludeTax')}</InputLabel>
                                    <Select
                                        fullWidth
                                        labelId="excludeTax"
                                        label={t('excludeTax')}
                                        name={"excludeTax"}
                                        onChange={handleChange}
                                        value={values?.excludeTax}
                                        // value={values?.excludeTax.length ? values.excludeTax : []}
                                        renderValue={(selected) => {

                                            return (
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {
                                                        selected.map((value, index) => {

                                                            const tax = dataTaxRate.filter(i => i.id === value)

                                                            const taxRate = tax.length ? `${tax[0].rate_name} (${parseFloat(tax[0].tax_percentage).toFixed(2)}%)` : '-'

                                                            return (
                                                                <Chip key={index} label={taxRate} />
                                                            )
                                                        })
                                                    }
                                                </Box>
                                            )

                                        }}
                                        sx={{
                                            textAlign: 'left',
                                            width: '65%'
                                        }}
                                        multiple
                                        input={<OutlinedInput id="select-multiple-chip" label="Chip" />}
                                    >
                                        {
                                            dataTaxRate.map((i, index) => {
                                                return (
                                                    <MenuItem
                                                        key={index}
                                                        value={i.id}
                                                    >
                                                        <Checkbox checked={values.excludeTax.indexOf(i.id) > -1} />
                                                        <ListItemText primary={`${i.rate_name} (${parseFloat(i.tax_percentage).toFixed(2)}%)`} />
                                                    </MenuItem>
                                                )
                                            })
                                        }
                                    </Select>
                                    <FormHelperText>
                                        {touched.excludeTax && errors.excludeTax}
                                    </FormHelperText>
                                </FormControl>
                            </Grid>
                        }
                    </Grid>
                </Grid>
            </>
        )
    }

    const RenderBranchList = () => {

        if (!values) return <></>

        return (
            <StyledCard>
                <Grid container spacing={0.5}>
                    <Grid item xs={12} sm={12} md={3} textAlign={"left"}>
                        <CommonSectionHeader
                            title={t('branches')}
                            subTitle={t('branchDetailsInformation')}
                        />
                    </Grid>

                    <Grid item xs={12} sm={12} md={9}>
                        {
                            RenderBranch()
                        }
                    </Grid>
                </Grid>
            </StyledCard>
        )
    }

    const RenderBranchUserList = () => {

        if (!values) return <></>

        return (
            <StyledCard
                sx={{
                    mt: 2
                }}
            >
                <Grid container spacing={0.5}>
                    <Grid item xs={12} sm={12} md={3} textAlign={"left"}>
                        <CommonSectionHeader
                            title={t('branchUsers')}
                            subTitle={t('branchUserDetailsInformation')}
                        />
                    </Grid>

                    <Grid item xs={12} sm={12} md={9}>

                        {
                            RenderBranchUser()
                        }

                    </Grid>
                </Grid>
            </StyledCard>
        )
    }

    const RenderBranch = () => {

        if (!dataBranches.length) return <RenderEmpty branchType={branchTypes.customer} parentId={id} />

        return (
            <StyledCard2>
                <List
                    sx={{
                        width: '100%',
                        position: 'relative',
                        overflow: 'auto',
                        minHeight: 300,
                        '& ul': { padding: 0 },
                    }}
                    disablePadding
                >
                    <StyledListHeader>{t('branches')}</StyledListHeader>
                    {
                        dataBranches.map((i, index) => (
                            <ListItem
                                key={`${index}`}
                                disablePadding
                                divider
                            >
                                <ListItemButton
                                    disableGutters
                                    sx={{
                                        px: 2
                                    }}
                                >
                                    <Grid container spacing={1}>
                                        <Grid item md={11} sm={11} xs={11} display={"flex"} justifyContent={"flex-start"} alignItems={"center"}
                                            sx={{
                                                fontSize: 15
                                            }}
                                        >
                                            {
                                                `${i?.branch_name} (${i?.branch_code})`
                                            }

                                            {
                                                i?.status === "0" &&
                                                <Tooltip
                                                    title={t("disabled")}
                                                >
                                                    <Box sx={{ ml: 1 }}>
                                                        <CommonIcons name={icons.disabled} sx={{ color: red[500] }} />
                                                    </Box>
                                                </Tooltip>
                                            }

                                            {
                                                i?.is_hq === "1" &&
                                                <Tooltip
                                                    title={t("mainBranch")}
                                                >
                                                    <Box sx={{ ml: 1 }}>
                                                        <CommonIcons name={icons.default} sx={{ color: "primary" }} />
                                                    </Box>
                                                </Tooltip>
                                            }

                                            {
                                                (i?.type === "branch" || i?.type === "branch_shipping") &&
                                                <Tooltip
                                                    title={t("branch")}
                                                >
                                                    <Box sx={{ ml: 1 }}>
                                                        <CommonIcons name={icons.warehouse} sx={{ color: "primary" }} />
                                                    </Box>
                                                </Tooltip>
                                            }

                                            {
                                                (i?.type === "branch_shipping" || i?.type === "shipping_address") &&
                                                <Tooltip
                                                    title={t("shippingAddress")}
                                                >
                                                    <Box sx={{ ml: 1 }}>
                                                        <CommonIcons name={icons.logistic} sx={{ color: "primary" }} />
                                                    </Box>
                                                </Tooltip>
                                            }

                                        </Grid>
                                        <Grid item md={1} sm={1} xs={1} display={"flex"} justifyContent={"center"} alignItems={"center"}>
                                            {
                                                isOwnProfile !== true &&

                                                isPermissionAllow({ scopeType: scopeTypes[scopeIds.userBranches.id].customer.id, functionType: functionTypes.pageUpdate.id }) &&

                                                <IconButton
                                                    color={"primary"}
                                                    size="small"
                                                    onClick={() => handleItemEdit({ branchType: branchTypes.customer, itemId: i.id })}
                                                >
                                                    <CommonIcons name={icons.edit} />
                                                </IconButton>
                                            }

                                            {
                                                isOwnProfile !== true &&

                                                isPermissionAllow({ scopeType: scopeTypes[scopeIds.userBranches.id].customer.id, functionType: functionTypes.remove.id }) &&

                                                <IconButton
                                                    color={"error"}
                                                    size="small"
                                                    onClick={() => handleItemDelete({ itemId: i.id, branchType: branchTypes.customer })}
                                                    sx={{
                                                        mr: 2
                                                    }}
                                                >
                                                    <CommonIcons name={icons.remove} />
                                                </IconButton>
                                            }
                                        </Grid>
                                    </Grid>
                                </ListItemButton>
                            </ListItem>
                        ))
                    }
                </List>

                {
                    isOwnProfile !== true &&

                    isPermissionAllow({ scopeType: scopeTypes[scopeIds.userBranches.id].customer.id, functionType: functionTypes.pageNew.id }) &&

                    <Box
                        sx={{ m: 1 }}
                    >
                        <Button
                            variant={"outlined"}
                            size="medium"
                            fullWidth
                            onClick={() => handleItemNew({ branchType: branchTypes.customer })}
                        >
                            <CommonIcons name={icons.add} />
                            <Box sx={{ ml: 1 }}>
                                {t('new')}
                            </Box>
                        </Button>
                    </Box>
                }
            </StyledCard2>
        )
    }

    const RenderBranchUser = () => {

        // console.log('dataBranchUsers', dataBranchUsers)

        if (!dataBranchUsers.length) return <RenderBranchUserEmpty branchUserType={branchUserTypes.customer} parentId={id} />

        return (
            <StyledCard2>
                <List
                    sx={{
                        width: '100%',
                        position: 'relative',
                        overflow: 'auto',
                        minHeight: 300,
                        '& ul': { padding: 0 },
                    }}
                    disablePadding
                >
                    <StyledListHeader>{t('users')}</StyledListHeader>
                    {
                        dataBranchUsers.map((i, index) => (
                            <ListItem
                                key={`${index}`}
                                disablePadding
                                divider
                            >
                                <ListItemButton
                                    disableGutters
                                    sx={{
                                        px: 2
                                    }}
                                >
                                    <Grid container spacing={1}>
                                        <Grid item md={11} sm={11} xs={11} display={"flex"} justifyContent={"flex-start"} alignItems={"center"}>
                                            {
                                                `${i?.firstname} ${i?.lastname} (${i.username})`
                                            }

                                            {
                                                i?.status !== "1" &&
                                                <Box sx={{ ml: 1 }}>
                                                    <CommonIcons name={icons.disabled} sx={{ color: red[600] }} />
                                                </Box>
                                            }

                                        </Grid>
                                        <Grid item md={1} sm={1} xs={1} display={"flex"} justifyContent={"center"} alignItems={"center"}>
                                            {
                                                isPermissionAllow({ scopeType: scopeTypes[scopeIds.branchUsers.id].customer.id, functionType: functionTypes.pageUpdate.id }) &&

                                                <IconButton
                                                    color={"primary"}
                                                    size="small"
                                                    onClick={() => handleItemEdit({ branchUserType: branchUserTypes.customer, itemId: i.user_id })}
                                                >
                                                    <CommonIcons name={icons.edit} />
                                                </IconButton>
                                            }

                                            {
                                                isPermissionAllow({ scopeType: scopeTypes[scopeIds.branchUsers.id].customer.id, functionType: functionTypes.remove.id }) &&

                                                <IconButton
                                                    color={"error"}
                                                    size="small"
                                                    onClick={() => handleItemDelete({ itemId: i.user_id, branchUserType: branchUserTypes.customer })}
                                                    sx={{
                                                        mr: 2
                                                    }}
                                                >
                                                    <CommonIcons name={icons.remove} />
                                                </IconButton>
                                            }
                                        </Grid>
                                    </Grid>
                                </ListItemButton>
                            </ListItem>
                        ))
                    }
                </List>

                {
                    isPermissionAllow({ scopeType: scopeTypes[scopeIds.branchUsers.id].customer.id, functionType: functionTypes.pageNew.id }) &&

                    <Box
                        sx={{ m: 1 }}
                    >
                        <Button
                            variant={"outlined"}
                            size="medium"
                            fullWidth
                            onClick={() => handleItemNew({ branchUserType: branchUserTypes.customer })}
                        >
                            <CommonIcons name={icons.add} />
                            <Box sx={{ ml: 1 }}>
                                {t('new')}
                            </Box>
                        </Button>
                    </Box>
                }
            </StyledCard2>
        )
    }

    const RenderForm = () => {

        if (!values) return <></>

        return (

            <form onSubmit={formik.handleSubmit}>
                <StyledCard>
                    <Grid container spacing={0.5}>
                        {
                            RenderFormAuth()
                        }

                        <Grid item xs={12}>
                            <Divider sx={{ my: 2 }} />
                        </Grid>

                        {
                            RenderFormCompany()
                        }

                        <Grid item xs={12}>
                            <Divider sx={{ my: 2 }} />
                        </Grid>

                        {
                            RenderFormUser()
                        }

                        <Grid item xs={12}>
                            <Divider sx={{ my: 2 }} />
                        </Grid>


                        {
                            RenderFormTax()
                        }

                        {
                            RenderCustomFieldSelection()
                        }
                    </Grid>
                </StyledCard>

                <Grid container spacing={1} sx={{ pl: 2, pr: 2 }}>
                    <Grid item md={12} textAlign={"right"}>
                        <LoadingButton
                            type="submit"
                            // fullWidth
                            variant="contained"
                            sx={{
                                mt: 3,
                                mb: 2
                            }}
                            loading={isLoading}
                        >
                            {t('saveChanges')}
                        </LoadingButton>
                    </Grid>

                </Grid>
            </form>

        )
    }

    return (
        <Box>
            {
                getHtmlHead({ routeType, title: headTitles.updateCustomer })
            }

            <CommonBreadcumbs
                routeType={routeType}
                pageTitle={t("updateCustomer")}
                breadcrumbs={[
                    {
                        displayText: t("customers"),
                        props: {
                            href: getRoute(`users/customers`)
                        }
                    },
                    {
                        displayText: t("updateCustomer"),
                        props: {
                            color: 'secondary.main'
                        }
                    },
                ]}
            />

            {

                (isPermissionAllow({ functionType: functionTypes.pageUpdate.id }) || isOwnProfile === true) ?
                    RenderForm()
                    :
                    <CommonRestricted />
            }

            {
                RenderBranchList()
            }

            {
                RenderBranchUserList()
            }

        </Box>
    )
}